/**
 * API-based AgentShield Middleware for Next.js
 *
 * This middleware uses the AgentShield API for detection and enforcement,
 * instead of running detection locally. This approach:
 *
 * 1. Works reliably in Edge Runtime (no WASM loading issues)
 * 2. Ensures consistent detection across all platforms
 * 3. Applies centralized policies from the dashboard
 * 4. Supports deny lists, thresholds, and path rules
 *
 * @example
 * ```typescript
 * // middleware.ts
 * import { withAgentShield } from '@kya-os/agentshield-nextjs/api-middleware';
 *
 * export default withAgentShield({
 *   apiKey: process.env.AGENTSHIELD_API_KEY!,
 *   // Optional overrides:
 *   onBlock: 'redirect', // 'block' | 'redirect' | 'challenge'
 *   redirectUrl: '/blocked',
 *   skipPaths: ['/api/health', '/_next/*'],
 * });
 *
 * export const config = {
 *   matcher: ['/((?!_next/static|favicon.ico).*)'],
 * };
 * ```
 */

import { NextRequest, NextResponse } from 'next/server';
import { AgentShieldClient, getAgentShieldClient, type EnforcementDecision } from './api-client';

// ============================================================================
// Types
// ============================================================================

/**
 * Middleware configuration
 */
export interface AgentShieldMiddlewareConfig {
  /** API key (or use AGENTSHIELD_API_KEY env var) */
  apiKey?: string;
  /** API base URL (defaults to production) */
  apiUrl?: string;
  /**
   * Use edge detection for lower latency (~30-50ms vs ~150ms) and better coverage.
   * Edge detection can identify non-JS clients (curl, Python, Claude Code WebFetch)
   * that the pixel cannot detect since they don't execute JavaScript.
   * Set to false to use the Vercel API instead.
   * @default true
   */
  useEdge?: boolean;
  /** Request timeout in ms (default: 5000) */
  timeout?: number;

  /**
   * Action to take when an agent should be blocked
   * - 'block': Return 403 response
   * - 'redirect': Redirect to redirectUrl
   * - 'challenge': Show a challenge page (future)
   * Default: uses policy from dashboard
   */
  onBlock?: 'block' | 'redirect' | 'challenge';

  /**
   * URL to redirect to when blocking (if onBlock is 'redirect')
   * Default: uses redirectUrl from dashboard policy
   */
  redirectUrl?: string;

  /**
   * Custom blocked response
   */
  blockedResponse?: {
    status?: number;
    message?: string;
    headers?: Record<string, string>;
  };

  /**
   * Paths to skip (in addition to dashboard policy)
   * Supports glob patterns: '/api/*', '/_next/*'
   */
  skipPaths?: string[];

  /**
   * Only enforce on these paths (overrides dashboard policy)
   */
  includePaths?: string[];

  /**
   * Callback when an agent is detected
   */
  onAgentDetected?: (request: NextRequest, decision: EnforcementDecision) => void | Promise<void>;

  /**
   * Callback to customize the blocked response
   */
  customBlockedResponse?: (
    request: NextRequest,
    decision: EnforcementDecision
  ) => NextResponse | Promise<NextResponse>;

  /**
   * Whether to fail open (allow) on API errors
   * Default: true (recommended for production)
   */
  failOpen?: boolean;

  /**
   * Enable debug logging
   */
  debug?: boolean;
}

// ============================================================================
// Path Matching
// ============================================================================

/**
 * Check if a path matches a pattern
 */
function matchPath(path: string, pattern: string): boolean {
  // Handle exact match
  if (pattern === path) return true;

  // Handle glob patterns
  if (pattern.includes('*')) {
    const regexPattern = pattern
      .replace(/[.+?^${}()|[\]\\]/g, '\\$&') // Escape regex chars
      .replace(/\*/g, '.*'); // Convert * to .*
    return new RegExp(`^${regexPattern}$`).test(path);
  }

  // Handle prefix match
  if (pattern.endsWith('/')) {
    return path.startsWith(pattern) || path === pattern.slice(0, -1);
  }

  return path.startsWith(pattern);
}

/**
 * Check if path should be skipped
 */
function shouldSkipPath(path: string, skipPaths: string[]): boolean {
  return skipPaths.some((pattern) => matchPath(path, pattern));
}

/**
 * Check if path should be included (if includePaths is set)
 */
function shouldIncludePath(path: string, includePaths?: string[]): boolean {
  if (!includePaths || includePaths.length === 0) return true;
  return includePaths.some((pattern) => matchPath(path, pattern));
}

// ============================================================================
// Response Builders
// ============================================================================

/**
 * Build blocked response
 */
function buildBlockedResponse(
  decision: EnforcementDecision,
  config: AgentShieldMiddlewareConfig
): NextResponse {
  const status = config.blockedResponse?.status ?? 403;
  const message = config.blockedResponse?.message ?? decision.message ?? 'Access denied';

  const response = NextResponse.json(
    {
      error: message,
      code: 'AGENT_BLOCKED',
      reason: decision.reason,
      agentType: decision.agentType,
    },
    { status }
  );

  // Add custom headers
  if (config.blockedResponse?.headers) {
    for (const [key, value] of Object.entries(config.blockedResponse.headers)) {
      response.headers.set(key, value);
    }
  }

  // Add AgentShield headers
  response.headers.set('X-AgentShield-Action', decision.action);
  response.headers.set('X-AgentShield-Reason', decision.reason);

  return response;
}

/**
 * Build redirect response
 */
function buildRedirectResponse(
  request: NextRequest,
  decision: EnforcementDecision,
  config: AgentShieldMiddlewareConfig
): NextResponse {
  const redirectUrl = config.redirectUrl || decision.redirectUrl || '/blocked';
  const url = new URL(redirectUrl, request.url);

  // Add query params with detection info
  url.searchParams.set('reason', decision.reason);
  if (decision.agentType) {
    url.searchParams.set('agent', decision.agentType);
  }

  return NextResponse.redirect(url);
}

// ============================================================================
// Middleware Factory
// ============================================================================

/**
 * Create AgentShield middleware with API-based detection
 *
 * @example
 * ```typescript
 * // middleware.ts
 * import { withAgentShield } from '@kya-os/agentshield-nextjs/api-middleware';
 *
 * export default withAgentShield({
 *   onBlock: 'block',
 *   skipPaths: ['/api/health'],
 * });
 * ```
 */
export function withAgentShield(config: AgentShieldMiddlewareConfig = {}) {
  // Initialize client (will use AGENTSHIELD_API_KEY env var if not provided)
  let client: AgentShieldClient | null = null;

  const getClient = () => {
    if (!client) {
      client = getAgentShieldClient({
        apiKey: config.apiKey,
        baseUrl: config.apiUrl,
        useEdge: config.useEdge,
        timeout: config.timeout,
        debug: config.debug,
      });
    }
    return client;
  };

  // Default skip paths (static assets, etc.)
  const defaultSkipPaths = [
    '/_next/static/*',
    '/_next/image/*',
    '/favicon.ico',
    '/robots.txt',
    '/sitemap.xml',
  ];

  const skipPaths = [...defaultSkipPaths, ...(config.skipPaths || [])];
  const failOpen = config.failOpen ?? true;

  return async function middleware(request: NextRequest): Promise<NextResponse> {
    const path = request.nextUrl.pathname;
    const startTime = Date.now();

    // Check skip paths
    if (shouldSkipPath(path, skipPaths)) {
      return NextResponse.next();
    }

    // Check include paths
    if (!shouldIncludePath(path, config.includePaths)) {
      return NextResponse.next();
    }

    try {
      const client = getClient();

      // Extract request context for potential logging
      const userAgent = request.headers.get('user-agent') || undefined;
      const ipAddress =
        request.ip ||
        request.headers.get('x-forwarded-for')?.split(',')[0]?.trim() ||
        request.headers.get('x-real-ip') ||
        undefined;

      // Call enforce API
      const result = await client.enforce({
        headers: Object.fromEntries(request.headers.entries()),
        userAgent,
        ipAddress,
        path,
        url: request.url,
        method: request.method,
        requestId: request.headers.get('x-request-id') || undefined,
        options: {
          // Always include detection results for logging (needed when using edge)
          includeDetectionResult: true,
        },
      });

      // Handle API error
      if (!result.success || !result.data) {
        if (config.debug) {
          console.warn('[AgentShield] API error:', result.error);
        }

        if (failOpen) {
          return NextResponse.next();
        }

        // Fail closed - block on error
        return NextResponse.json(
          { error: 'Security check failed', code: 'API_ERROR' },
          { status: 503 }
        );
      }

      const decision = result.data.decision;

      // Log if debug enabled
      if (config.debug) {
        console.log('[AgentShield] Decision:', {
          path,
          action: decision.action,
          isAgent: decision.isAgent,
          confidence: decision.confidence,
          agentName: decision.agentName,
          detectionMethod: result.data.detection?.detectionMethod || 'not-included',
          processingTimeMs: Date.now() - startTime,
        });
      }

      // Fire-and-forget logging - ONLY when using Gateway Worker (edge detection)
      // When useEdge: false, the /api/v1/enforce endpoint already logs to the database
      // This prevents double-logging while ensuring Gateway detections are persisted
      if (client.isUsingEdge() && result.data.detection) {
        client
          .logDetection({
            detection: result.data.detection,
            context: { userAgent, ipAddress, path, url: request.url, method: request.method },
          })
          .catch((err) => {
            if (config.debug) {
              console.error('[AgentShield] Log detection failed:', err);
            }
          });
      }

      // Handle agent detection callback
      if (decision.isAgent && config.onAgentDetected) {
        await config.onAgentDetected(request, decision);
      }

      // Handle enforcement action
      switch (decision.action) {
        case 'block': {
          // Use custom response if provided
          if (config.customBlockedResponse) {
            return config.customBlockedResponse(request, decision);
          }

          // Check if config overrides to redirect
          if (config.onBlock === 'redirect') {
            return buildRedirectResponse(request, decision, config);
          }

          return buildBlockedResponse(decision, config);
        }

        case 'redirect': {
          return buildRedirectResponse(request, decision, config);
        }

        case 'challenge': {
          // Future: implement challenge page
          // For now, treat as redirect
          return buildRedirectResponse(request, decision, config);
        }

        case 'log':
        case 'allow':
        default: {
          // Allow the request to proceed
          const response = NextResponse.next();

          // Add detection headers for downstream use
          if (decision.isAgent) {
            response.headers.set('X-AgentShield-Detected', 'true');
            response.headers.set('X-AgentShield-Confidence', decision.confidence.toString());
            if (decision.agentName) {
              response.headers.set('X-AgentShield-Agent', decision.agentName);
            }
          }

          return response;
        }
      }
    } catch (error) {
      // Unexpected error
      if (config.debug) {
        console.error('[AgentShield] Middleware error:', error);
      }

      if (failOpen) {
        return NextResponse.next();
      }

      return NextResponse.json(
        { error: 'Security check failed', code: 'MIDDLEWARE_ERROR' },
        { status: 503 }
      );
    }
  };
}

/**
 * Convenience export for simple setup
 *
 * @example
 * ```typescript
 * // middleware.ts
 * export { agentShieldMiddleware as default } from '@kya-os/agentshield-nextjs/api-middleware';
 * ```
 */
export const agentShieldMiddleware = withAgentShield();
